/**
 * Lightweight localStorage cache with TTL for API responses.
 *
 * TTL constants (in milliseconds):
 *   CACHE_SHORT  :  1 min  — données fréquemment mises à jour (activités)
 *   CACHE_TTL    :  5 min  — données standard (audits, sessions)
 *   CACHE_LONG   : 30 min  — données rarement modifiées (permissions, rôles)
 */

export const CACHE_SHORT = 60 * 1000;
export const CACHE_TTL   = 5 * 60 * 1000;
export const CACHE_LONG  = 30 * 60 * 1000;

/**
 * Read an entry from cache. Returns null if missing or expired.
 */
export function cacheGet(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, expiresAt } = JSON.parse(raw);
    if (Date.now() > expiresAt) {
      localStorage.removeItem(key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/**
 * Write an entry to cache with a TTL (in ms).
 */
export function cacheSet(key, data, ttl = CACHE_TTL) {
  try {
    localStorage.setItem(key, JSON.stringify({ data, expiresAt: Date.now() + ttl }));
  } catch {
    // localStorage full or unavailable — silently skip
  }
}

/**
 * Delete a single cache entry.
 */
export function cacheDelete(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * Delete all entries whose key starts with a given prefix.
 */
export function cacheDeletePrefix(prefix) {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(prefix))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

/**
 * Cache-aside helper: return cached value or call fetchFn(), store and return the result.
 *
 * @param {string}   key       - cache key
 * @param {Function} fetchFn   - async function that returns the data to cache
 * @param {number}   ttl       - TTL in ms (default CACHE_TTL = 5 min)
 */
export async function cacheGetOrFetch(key, fetchFn, ttl = CACHE_TTL) {
  const cached = cacheGet(key);
  if (cached !== null) return cached;
  const data = await fetchFn();
  cacheSet(key, data, ttl);
  return data;
}
